import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useBreakpoint } from "../custom-hooks/useBreakpoint";
import { IEventFlat } from "../domain/models/Events";
import eventPlaceholder from "../assets/events-1.png";
import { environment } from "../environments/environment";

function stripHtmlToText(html: string): string {
    if (!html) return "";
    try {
        if (typeof window !== "undefined" && "DOMParser" in window) {
            const doc = new window.DOMParser().parseFromString(html, "text/html");
            return (doc.body.textContent || "").trim();
        }
    } catch {
    }
    return html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

const Badge = ({ text }: { text: string }) => (
    <div className="py-[7px] px-[8px] border-[1.5px] border-primary-400 rounded-[10px] whitespace-nowrap">
        <p className="text-base md:text-xl font-medium uppercase text-center text-primary-400">{text}</p>
    </div>
);

const RegisterButton = ({ text }: { text: string }) => (
    <button className="w-full max-w-[640px] h-[54px] px-5 py-4 border-2 border-primary-400 rounded-[4px] uppercase text-base font-normal text-primary-400 text-center mt-auto">
        {text}
    </button>
);

type EventCardProps = {
    title: string;
    subtitle: string;
    date: string;
    location: string;
    imageUrl?: string | null;
    description: string;
};

const EventCard = ({
    title,
    subtitle,
    date,
    location,
    imageUrl,
    description,
    whenWhereTitle,
    registerText,
    isSmallScreen,
    isTablet,
    isDesktopMedium,
}: EventCardProps & {
    whenWhereTitle: string;
    registerText: string;
    isSmallScreen: boolean;
    isTablet?: boolean;
    isDesktopMedium?: boolean;
}) => {
    const cardBase =
        "mt-0 bg-background-100 shadow-[0px_4px_4px_0px_#36335214] rounded-[6px] flex flex-col gap-[24px] overflow-hidden";

    const cardClass = isTablet
        ? `${cardBase} w-[688px] h-[647px] px-[24px] py-[20px] mx-auto`
        : isSmallScreen
            ? `${cardBase} w-full max-w-[768px] h-[647px] px-[15px] py-[24px]`
            : isDesktopMedium
                ? `${cardBase} w-full h-[647px] px-[22px] py-[20px]`
                : `${cardBase} w-full h-[647px] px-[24px] py-[20px]`;

    const imgClass = isTablet
        ? "w-full h-[250px] object-cover rounded-[5px]"
        : isSmallScreen
            ? "w-full h-[250px] object-cover rounded-[5px]"
            : isDesktopMedium
                ? "w-full h-[250px] object-cover rounded-[5px]"
                : "w-full h-[250px] object-cover rounded-[5px]";

    const descriptionText = stripHtmlToText(description);

    return (
        <div className={cardClass}>
            <img src={imageUrl || eventPlaceholder} alt={title} className={imgClass} />
            <div className="flex flex-col flex-grow">
                <div className="flex flex-col gap-2 w-full">
                    <h3 className="text-2xl text-primary-400 font-medium line-clamp-2">{title}</h3>
                    {subtitle && (
                        <p className="text-lg text-primary-400 font-normal line-clamp-2">
                            {subtitle}
                        </p>
                    )}
                </div>
                <div className={`flex flex-col gap-3 w-full ${subtitle ? "mt-6" : ""}`}>
                    <h4 className="text-lg font-semibold text-primary-400">{whenWhereTitle}</h4>
                    <div className="flex gap-2">
                        <Badge text={date} />
                        <Badge text={location} />
                    </div>
                </div>

                <p
                    className={
                        isSmallScreen
                            ? "w-full text-base md:text-lg text-primary-400 font-normal mt-4 line-clamp-6 leading-[130%]"
                            : "w-full text-lg mt-4 text-primary-400 font-normal line-clamp-6 leading-[130%]"
                    }
                >
                    {descriptionText}
                </p>
            </div>
            <div className="hidden">
                <RegisterButton text={registerText} />
            </div>
        </div>
    );
};

function formatEventDate(date: Date | string): string {
    if (!date) return "";
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleString("uk-UA", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
    });
}

export default function EventCardsBlock({ events }: { events: IEventFlat[] }) {
    const { t } = useTranslation();
    const whenWhereTitle = t("home.eventsWhenWhereTitle");
    const registerText = t("buttons.register");

    const { isMobile, isTablet, isDesktopSmall, isDesktopMedium, isDesktopLarge } = useBreakpoint();
    const [activeIndex, setActiveIndex] = useState(0);

    const isSmallScreen = isMobile || isTablet;
    const containerWidth = isMobile ? 360 : isTablet ? "100%" : isDesktopSmall ? 936 : isDesktopMedium ? 1180 : 1280;

    const maxEventsCountValue = Number.parseInt(String(environment.EVENTS_MAX_COUNT ?? ""), 10);
    const maxEventsCount = Number.isFinite(maxEventsCountValue) && maxEventsCountValue > 0 ? maxEventsCountValue : 4;
    const eventsToShow = events.slice(0, maxEventsCount);
    const cards = eventsToShow.map((event) => ({
        title: event?.title || "",
        subtitle: event?.subtitle || "",
        date: formatEventDate(event?.eventDate),
        location: t(`events.location.${event?.location}`),
        imageUrl: event?.pictureExists?.() ? event?.getPictureUrl?.() : null,
        description: event?.description || "",
    }));
    const dotsToShow = cards.length;

    const sectionClass = isTablet
        ? "w-screen -mx-[40px] px-[40px] relative"
        : isMobile
            ? "w-screen -mx-[15px] px-[15px] relative"
            : "relative";

    const dotsWrapperClass = isTablet
        ? "w-screen -mx-[40px] px-[40px] flex justify-center items-center gap-3 mt-[32px] mb-[18px]"
        : isMobile
            ? "w-screen -mx-[15px] px-[15px] flex justify-center items-center gap-3 mt-8 mb-8"
            : "flex justify-center items-center gap-3 mt-4";

    return (
        <section className={sectionClass}>
            <div className="mx-auto" style={{ width: containerWidth }}>
                <div
                    className={
                        isSmallScreen
                            ? "flex justify-center mt-[26px]"
                            : "grid grid-cols-2 gap-6"
                    }
                >
                    {isSmallScreen ? (
                        <EventCard
                            {...(cards[activeIndex] || {
                                title: "",
                                subtitle: "",
                                date: "",
                                location: "",
                                imageUrl: null,
                                description: "",
                            })}
                            whenWhereTitle={whenWhereTitle}
                            registerText={registerText}
                            isSmallScreen
                            isTablet={isTablet}
                        />
                    ) : (
                        cards.map((card, index) => (
                            <EventCard
                                key={index}
                                {...card}
                                whenWhereTitle={whenWhereTitle}
                                registerText={registerText}
                                isSmallScreen={false}
                                isDesktopMedium={isDesktopMedium || isDesktopLarge}
                            />
                        ))
                    )}
                </div>
            </div>

            {isSmallScreen && dotsToShow > 1 && (
                <div
                    className={dotsWrapperClass}
                    style={
                        !isTablet && !isMobile
                            ? { width: containerWidth, height: 12, marginLeft: "auto", marginRight: "auto" }
                            : undefined
                    }
                >
                    {Array.from({ length: dotsToShow }).map((_, i) => (
                        <div
                            key={i}
                            onClick={() => setActiveIndex(i)}
                            className={`w-3 h-3 rounded-full cursor-pointer transition-colors duration-300 ${i === activeIndex ? "bg-white" : "bg-primary-500"
                                }`}
                        />
                    ))}
                </div>
            )}
        </section>
    );
}
